import React, { useState } from 'react';
import {
  ASSET_TYPE,
  AssetItem,
  AssetReport,
  AssetType,
  LIABILITY_TYPE,
  LiabilityItem,
  LiabilityType,
  REPORT_ITEM_TYPE,
  REPORT_OWNER_TYPE,
  ReportOwnerType,
} from '../../type/AssetReport.type';
import ExcelJS, { Style } from 'exceljs';
import fileDownload from 'js-file-download';
import { ASSET_REPORT_LABEL } from '../../../../common/comstant/LabelConstants';
import { useAppStateAssetReportListStore } from '../../../../common/store/AppStateStore.zustand';
import { getDefaultUploadCategory2 } from '../../util/AddAssetReportModal.util';

export interface AddAssetReportModalInputData {
  newAssetReport: AssetReport;
  handleNewAssetReportChange: (target: string, value: any) => void;
}

const AddAssetReportModalInput = ({ newAssetReport, handleNewAssetReportChange }: AddAssetReportModalInputData) => {
  const { assetReportList } = useAppStateAssetReportListStore();
  const [excelType, setExcelType] = useState<string>('basic');

  const handleSampleFileDownloadClick = async () => {
    // excel-dependent-dropdown.js
    const workbook = new ExcelJS.Workbook();
    workbook.creator = 'Your Name';
    workbook.created = new Date();

    // —————— 메인 시트 ——————
    const sheet = workbook.addWorksheet('Sheet1');

    // —————— 리스트 전용 시트 (숨김) ——————
    const listSheet = workbook.addWorksheet('Lists');
    listSheet.state = 'hidden';

    // 1열: 자산 리스트, 2열: 부채 리스트
    listSheet.getColumn(1).values = [
      '자산', // 헤더(이름: “자산”)
      '저축',
      '투자',
      '지출예정',
      '기타',
    ];
    listSheet.getColumn(2).values = [
      '부채', // 헤더(이름: “부채”)
      '대출',
      '지출예정',
      '기타',
    ];

    // “자산” / “부채” 라는 이름으로 범위 정의
    workbook.definedNames.add('자산', 'Lists!$A$2:$A$5');
    workbook.definedNames.add('부채', 'Lists!$B$2:$B$4');

    const headerStyle = {
      font: {
        bold: true,
      },
      alignment: {
        vertical: 'middle',
        horizontal: 'center',
      },
      border: {
        top: {
          style: 'thin',
        },
        left: {
          style: 'thin',
        },
        bottom: {
          style: 'thin',
        },
        right: {
          style: 'thin',
        },
      },
    };

    // 컬럼 설정
    sheet.columns = [
      {
        header: '구분1',
        key: 'category1',
        width: 15,
      },
      { header: '구분2', key: 'category2', width: 15 },
      { header: '이름', key: 'name', width: 30 },
      { header: '금액', key: 'amount', width: 30 },
      { header: '원금 (투자자산인 경우)', key: 'principal', width: 30 },
      { header: '배당금 (해당 월)', key: 'dividend', width: 30 },
    ];

    sheet.getCell('A1').style = headerStyle as Style;
    sheet.getCell('C1').style = headerStyle as Style;
    sheet.getCell('D1').style = headerStyle as Style;
    sheet.getCell('E1').style = headerStyle as Style;
    sheet.getCell('F1').style = headerStyle as Style;

    sheet.addRow({ category1: '자산', category2: '저축', name: 'ㅇㅇ은행 예금', amount: 120000 });
    sheet.addRow({
      category1: '자산',
      category2: '투자',
      name: '한국전자',
      amount: 120000,
      principal: 100000,
      dividend: 3000,
    });
    sheet.addRow({ category1: '부채', category2: '대출', name: 'ㅇㅇ은행 신용대출', amount: 85000 });

    for (let rowIndex = 2; rowIndex <= 100; rowIndex++) {
      // “구분1” 드롭다운: 고정 리스트
      const category1Cell = sheet.getCell(`A${rowIndex}`);
      category1Cell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: ['"자산,부채"'],
        showInputMessage: true,
        promptTitle: '구분1 선택',
        prompt: '“자산” 또는 “부채”를 선택하세요.',
        showErrorMessage: true,
        errorTitle: '잘못된 값',
        error: '“자산” 또는 “부채”만 입력 가능합니다.',
      };

      const category2Cell = sheet.getCell(`B${rowIndex}`);
      category2Cell.dataValidation = {
        type: 'list',
        allowBlank: false,
        formulae: [`=INDIRECT(IF(A${rowIndex} = "자산", "Lists!A2:A5", "Lists!B2:B4"))`],
        showInputMessage: true,
        promptTitle: '구분2 선택',
        prompt: '구분1에 따라 목록이 달라집니다.',
        showErrorMessage: true,
        errorTitle: '잘못된 값',
        error: '리스트에서 선택해주세요.',
      };
    }

    const buffer = await workbook.xlsx.writeBuffer();
    fileDownload(buffer, 'sample.xlsx');
  };

  const handleFileChangeBasic = (owner: ReportOwnerType, files: FileList | null) => {
    if (!files) return;
    const file = files.item(0);
    if (!file) return;
    const wb = new ExcelJS.Workbook();
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      const buffer = reader.result;
      if (!buffer) return;

      wb.xlsx.load(buffer as ArrayBuffer).then((workbook) => {
        workbook.eachSheet((sheet, id) => {
          if (sheet.name === 'Sheet1') {
            const reportItemList: (AssetItem | LiabilityItem)[] = [];
            sheet.eachRow((row, rowIndex) => {
              if (rowIndex === 1) return;
              const rowValues = row.values as ExcelJS.CellValue[];
              const uploadCategory1 =
                rowValues.at(1) == ASSET_REPORT_LABEL.ASSET ? REPORT_ITEM_TYPE.ASSET : REPORT_ITEM_TYPE.LIABILITY;
              let uploadCategory2;
              if (uploadCategory1 === REPORT_ITEM_TYPE.ASSET) {
                if (rowValues.at(2) == ASSET_REPORT_LABEL.SAVINGS) {
                  uploadCategory2 = ASSET_TYPE.SAVINGS;
                } else if (rowValues.at(2) == ASSET_REPORT_LABEL.INVESTMENT) {
                  uploadCategory2 = ASSET_TYPE.INVESTMENT;
                } else if (rowValues.at(2) == ASSET_REPORT_LABEL.EXPECT_TO_CONSUME) {
                  uploadCategory2 = ASSET_TYPE.EXPECT_TO_CONSUME;
                } else {
                  uploadCategory2 = ASSET_TYPE.ETC;
                }
              } else {
                if (rowValues.at(2) == ASSET_REPORT_LABEL.LOAN) {
                  uploadCategory2 = LIABILITY_TYPE.LOAN;
                } else if (rowValues.at(2) == ASSET_REPORT_LABEL.EXPECT_TO_CONSUME) {
                  uploadCategory2 = LIABILITY_TYPE.EXPECT_TO_CONSUME;
                } else {
                  uploadCategory2 = LIABILITY_TYPE.ETC;
                }
              }
              const title = (rowValues.at(3) as string) ?? '';
              const amount = (rowValues.at(4) as number) ?? 0;
              const principalAmount = (rowValues.at(5) as number) ?? amount;
              const dividendAmount = (rowValues.at(6) as number) ?? 0;
              const returnRate = isNaN(principalAmount / amount) ? 0 : principalAmount / amount;

              const prevReportItem = assetReportList
                .flatMap((assetReport) => assetReport.reportItemList)
                .find((reportItem) => reportItem.type === uploadCategory1 && reportItem.name === (title as string));
              let reportItem: AssetItem | LiabilityItem;
              if (uploadCategory1 === REPORT_ITEM_TYPE.ASSET) {
                reportItem = {
                  owner: owner,
                  name: title,
                  comment: '',
                  type: uploadCategory1,
                  amount: amount,
                  excludeYn: prevReportItem ? prevReportItem.excludeYn : false,
                  assetType: uploadCategory2,
                  principalAmount: uploadCategory2 === ASSET_TYPE.INVESTMENT ? principalAmount : 0,
                  returnRate: returnRate,
                  dividendAmount: uploadCategory2 === ASSET_TYPE.INVESTMENT ? dividendAmount : 0,
                  prevExistYn: prevReportItem ? true : false,
                } as AssetItem;
              } else {
                reportItem = {
                  owner: owner,
                  name: title,
                  comment: '',
                  type: uploadCategory1,
                  amount: amount,
                  excludeYn: prevReportItem ? prevReportItem.excludeYn : false,
                  liabilityType: uploadCategory2,
                  prevExistYn: prevReportItem ? true : false,
                } as LiabilityItem;
              }
              reportItemList.push(reportItem);
            });
            handleNewAssetReportChange('reportItemList', [
              ...newAssetReport.reportItemList.filter((reportItem) => reportItem.owner !== owner),
              ...reportItemList,
            ]);
          }
        });
      });
    };
  };

  const handleFileChangeBankSalad = (owner: ReportOwnerType, files: FileList | null) => {
    if (!files) return;
    const file = files.item(0);
    if (!file) return;
    const wb = new ExcelJS.Workbook();
    const reader = new FileReader();

    reader.readAsArrayBuffer(file);
    reader.onload = () => {
      const buffer = reader.result;
      if (!buffer) return;

      wb.xlsx.load(buffer as ArrayBuffer).then((workbook) => {
        workbook.eachSheet((sheet, id) => {
          if (sheet.name === '뱅샐현황') {
            let assetFlag = false;
            let assetLiabilityIndexDiff = 0;
            let categoryIndex = 0;
            let titleIndex = 0;
            let amountIndex = 0;
            const reportItemList: (AssetItem | LiabilityItem)[] = [];

            let investmentFlag = false;
            let investmentTitleIndex = 0;
            let principalAmountIndex = 0;
            let marketAmountIndex = 0;
            let returnRateIndex = 0;
            sheet.eachRow((row, rowIndex) => {
              const rowValues = row.values as ExcelJS.CellValue[];
              if (rowValues.some((rowValue) => rowValue === '자산')) {
                assetLiabilityIndexDiff =
                  rowValues.findIndex((rowValue, i) => rowValue === '부채') -
                  rowValues.findIndex((rowValue, i) => rowValue === '자산');
                assetFlag = true;
                return;
              }
              if (rowValues.some((rowValue) => rowValue === '총자산')) {
                assetFlag = false;
              }
              if (assetFlag && rowValues.some((rowValue, i) => rowValue === '항목')) {
                categoryIndex = rowValues.findIndex((rowValue, i) => rowValue === '항목');
                titleIndex = rowValues.findIndex((rowValue, i) => rowValue === '상품명');
                amountIndex = rowValues.findIndex((rowValue, i) => rowValue === '금액');
                return;
              }
              if (assetFlag) {
                const prevReportItem1 = assetReportList
                  .flatMap((assetReport) => assetReport.reportItemList)
                  .find(
                    (reportItem) =>
                      reportItem.type === REPORT_ITEM_TYPE.ASSET &&
                      reportItem.name === (rowValues[titleIndex] as string),
                  );
                const assetType = prevReportItem1
                  ? (prevReportItem1 as AssetItem).assetType
                  : getDefaultUploadCategory2(rowValues[categoryIndex] as string);
                const assetItem: AssetItem = {
                  id: '',
                  assetReportId: newAssetReport.id,
                  owner: owner,
                  name: rowValues[titleIndex] as string,
                  comment: '',
                  type: REPORT_ITEM_TYPE.ASSET,
                  amount: rowValues[amountIndex] as number,
                  excludeYn: prevReportItem1 ? prevReportItem1.excludeYn : false,
                  assetType: assetType as AssetType,
                  principalAmount: assetType === ASSET_TYPE.INVESTMENT ? (rowValues[amountIndex] as number) : 0,
                  returnRate: 0,
                  dividendAmount: 0,
                  prevExistYn: prevReportItem1 ? true : false,
                };
                const prevReportItem2 = assetReportList
                  .flatMap((assetReport) => assetReport.reportItemList)
                  .find(
                    (reportItem) =>
                      reportItem.type === REPORT_ITEM_TYPE.LIABILITY &&
                      reportItem.name === (rowValues[titleIndex + assetLiabilityIndexDiff] as string),
                  );
                const liabilityItem: LiabilityItem = {
                  id: '',
                  assetReportId: newAssetReport.id,
                  owner: owner,
                  name: rowValues[titleIndex + assetLiabilityIndexDiff] as string,
                  comment: '',
                  type: REPORT_ITEM_TYPE.LIABILITY,
                  amount: rowValues[amountIndex + assetLiabilityIndexDiff] as number,
                  excludeYn: prevReportItem2 ? prevReportItem2.excludeYn : false,
                  liabilityType: prevReportItem2
                    ? (prevReportItem2 as LiabilityItem).liabilityType
                    : (getDefaultUploadCategory2(
                        rowValues[categoryIndex + assetLiabilityIndexDiff] as string,
                      ) as LiabilityType),
                  prevExistYn: prevReportItem2 ? true : false,
                };

                reportItemList.push(assetItem);
                reportItemList.push(liabilityItem);
              }

              if (rowValues.some((rowValue, i) => rowValue === '수익률')) {
                investmentFlag = true;
                investmentTitleIndex = rowValues.findIndex((rowValue, i) => rowValue === '상품명');
                principalAmountIndex = rowValues.findIndex((rowValue, i) => rowValue === '투자원금');
                marketAmountIndex = rowValues.findIndex((rowValue, i) => rowValue === '평가금액');
                returnRateIndex = rowValues.findIndex((rowValue, i) => rowValue === '수익률');
                return;
              }
              if (rowValues.some((rowValue) => rowValue === '총 수익률')) {
                investmentFlag = false;
              }

              if (investmentFlag) {
                const investmentTitle = rowValues[investmentTitleIndex] as string;
                const principalAmount = rowValues[principalAmountIndex] as number;
                const marketAmount = rowValues[marketAmountIndex] as number;
                const returnRate = rowValues[returnRateIndex] as number;
                const targetItem = reportItemList.find(
                  (reportItem) => reportItem.name === investmentTitle && reportItem.amount === marketAmount,
                ) as AssetItem;
                if (targetItem) {
                  targetItem.principalAmount = isNaN(principalAmount) ? targetItem.amount : principalAmount;
                  targetItem.returnRate = isNaN(returnRate) ? 0 : returnRate;
                }
              }
            });
            handleNewAssetReportChange('reportItemList', [
              ...newAssetReport.reportItemList.filter((reportItem) => reportItem.owner !== owner),
              ...reportItemList.filter(
                (reportItem) => !!reportItem.name && !isNaN(Number(reportItem.amount)) && Number(reportItem.amount) > 0,
              ),
            ]);
          }
        });
      });
    };
  };

  return (
    <div className="p-6">
      <div className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">년월 선택</label>
          <input
            type="month"
            value={newAssetReport.yearMonth}
            onChange={(e) => handleNewAssetReportChange('yearMonth', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">엑셀 데이터 형식</label>
            <div className="mb-3">
              <div className="flex items-center space-x-4 mb-2">
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="excel-type"
                    value="basic"
                    className="form-radio text-blue-600"
                    checked={excelType === 'basic'}
                    onChange={(e) => setExcelType(e.target.value)}
                  />
                  <span className="ml-2 text-sm text-gray-700">기본 양식 엑셀</span>
                </label>
                <label className="inline-flex items-center">
                  <input
                    type="radio"
                    name="excel-type"
                    value="banksalad"
                    className="form-radio text-blue-600"
                    checked={excelType === 'banksalad'}
                    onChange={(e) => setExcelType(e.target.value)}
                  />
                  <span className="ml-2 text-sm text-gray-700">뱅크샐러드 엑셀</span>
                </label>
              </div>
              <div className="text-xs text-gray-500 mb-2">
                <div className="flex items-center">
                  <i className="fas fa-info-circle mr-1 text-blue-500"></i>
                  <span>엑셀 파일 형식을 선택해주세요</span>
                </div>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">남편 엑셀 파일</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <i className="fas fa-file-excel text-gray-400 text-3xl mb-3"></i>
                  <div className="flex flex-col items-center text-sm text-gray-600">
                    <label
                      htmlFor="husband-file"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>파일 업로드</span>
                      <input
                        id="husband-file"
                        name="husband-file"
                        type="file"
                        className="sr-only"
                        accept=".xlsx"
                        onChange={(e) => {
                          excelType === 'basic'
                            ? handleFileChangeBasic(REPORT_OWNER_TYPE.HUSBAND, e.target.files)
                            : handleFileChangeBankSalad(REPORT_OWNER_TYPE.HUSBAND, e.target.files);
                          e.target.value = '';
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Excel 파일만 가능</p>
                </div>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">아내 엑셀 파일</label>
              <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md">
                <div className="space-y-1 text-center">
                  <i className="fas fa-file-excel text-gray-400 text-3xl mb-3"></i>
                  <div className="flex flex-col items-center text-sm text-gray-600">
                    <label
                      htmlFor="wife-file"
                      className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500"
                    >
                      <span>파일 업로드</span>
                      <input
                        id="wife-file"
                        name="wife-file"
                        type="file"
                        className="sr-only"
                        accept=".xlsx"
                        onChange={(e) =>
                          excelType === 'basic'
                            ? handleFileChangeBasic(REPORT_OWNER_TYPE.WIFE, e.target.files)
                            : handleFileChangeBankSalad(REPORT_OWNER_TYPE.WIFE, e.target.files)
                        }
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500 mt-2">Excel 파일만 가능</p>
                </div>
              </div>
            </div>
          </div>
          {excelType === 'basic' && (
            <div className="mt-4 text-center">
              <button
                className="text-blue-600 hover:text-blue-500 text-sm font-medium"
                onClick={handleSampleFileDownloadClick}
              >
                <i className="fas fa-download mr-1"></i> 샘플 양식 다운로드
              </button>
            </div>
          )}
          {excelType === 'banksalad' && (
            <div className="mt-4 p-4 bg-gray-50 rounded-md">
              <div className="flex items-center text-sm text-gray-600 break-keep">
                <i className="fas fa-info-circle mr-2 text-blue-500 "></i>
                <span>뱅크샐러드 내보내기를 통해 다운로드한 엑셀 파일을 업로드해주세요.</span>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddAssetReportModalInput;
